import LegalLayout from '../components/LegalLayout';
import { Shield, Heart, AlertTriangle, Ban, Flag, Users } from 'lucide-react';

export default function GuidelinesPage() {
    return (
        <LegalLayout title="Community Guidelines" lastUpdated="November 23, 2025">
            <p className="text-gray-300 text-lg mb-8">
                DareUp is a community where creators challenge themselves and each other in fun, creative ways. To keep
                our platform safe and enjoyable for everyone, we ask all users to follow these Community Guidelines.
            </p>

            <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-6 mb-10">
                <p className="text-primary-400 font-semibold">
                    Violating these guidelines may result in content removal, account restrictions, or permanent bans.
                </p>
            </div>

            <GuidelineSection icon={<Shield className="w-6 h-6" />} title="Safety First" color="green">
                <p>
                    Your safety and the safety of others is our top priority. When creating or participating in dares:
                </p>
                <ul>
                    <li>Never create dares that could cause physical harm or injury</li>
                    <li>Don't encourage dangerous stunts or reckless behavior</li>
                    <li>Avoid dares involving vehicles, heights, fire, or other hazards</li>
                    <li>Don't participate in challenges that put yourself or others at risk</li>
                    <li>Never involve unwilling participants in your dares</li>
                </ul>
                <div className="bg-dark-800 rounded-lg p-4 mt-4">
                    <p className="text-green-400 font-semibold mb-2">Good examples:</p>
                    <p className="text-gray-400">
                        Creative art challenges, dance moves, cooking experiments, talent showcases, comedy sketches,
                        voice impressions
                    </p>
                </div>
            </GuidelineSection>

            <GuidelineSection icon={<Heart className="w-6 h-6" />} title="Respect & Kindness" color="pink">
                <p>Treat everyone with respect. We have zero tolerance for:</p>
                <ul>
                    <li>Bullying, harassment, or targeting specific individuals</li>
                    <li>Hate speech based on race, ethnicity, religion, gender, sexual orientation, or disability</li>
                    <li>Threats of violence or encouraging harm to others</li>
                    <li>Doxxing or sharing someone's personal information</li>
                    <li>Mocking or degrading other users</li>
                </ul>
                <p className="mt-4">
                    Remember: there's a real person behind every account. Be the kind of creator you'd want to follow.
                </p>
            </GuidelineSection>

            <GuidelineSection icon={<Ban className="w-6 h-6" />} title="Prohibited Content" color="red">
                <p>The following content is strictly prohibited and will be removed immediately:</p>
                <ul>
                    <li>
                        <strong>Sexual content:</strong> Nudity, sexually explicit material, or sexual solicitation
                    </li>
                    <li>
                        <strong>Violence:</strong> Graphic violence, gore, or content promoting violence
                    </li>
                    <li>
                        <strong>Illegal activities:</strong> Drug use, underage drinking, theft, vandalism
                    </li>
                    <li>
                        <strong>Harmful misinformation:</strong> Dangerous health advice, conspiracy theories that could
                        cause harm
                    </li>
                    <li>
                        <strong>Spam:</strong> Repetitive content, fake engagement, misleading links
                    </li>
                    <li>
                        <strong>Impersonation:</strong> Pretending to be someone else or a brand
                    </li>
                </ul>
            </GuidelineSection>

            <GuidelineSection
                icon={<AlertTriangle className="w-6 h-6" />}
                title="Copyright & Intellectual Property"
                color="yellow"
            >
                <p>Respect others' creative work:</p>
                <ul>
                    <li>Only use music you have the rights to use (your own music or royalty-free)</li>
                    <li>Don't repost others' videos without permission</li>
                    <li>Give credit when using others' ideas or content</li>
                    <li>Don't use copyrighted characters, logos, or branded content without authorization</li>
                </ul>
                <div className="bg-dark-800 rounded-lg p-4 mt-4">
                    <p className="text-yellow-400 font-semibold mb-2">Copyright Strikes:</p>
                    <ul className="text-gray-400 space-y-1">
                        <li>1st violation: Warning + content muted/removed</li>
                        <li>2nd violation: 7-day upload restriction</li>
                        <li>3rd violation: Permanent upload ban</li>
                    </ul>
                </div>
            </GuidelineSection>

            <GuidelineSection icon={<Users className="w-6 h-6" />} title="Protecting Minors" color="blue">
                <p>We take the safety of young users seriously:</p>
                <ul>
                    <li>Users must be 13 or older to use DareUp</li>
                    <li>Content featuring minors must be appropriate and safe</li>
                    <li>Never create content that sexualizes or endangers minors</li>
                    <li>Don't contact minors in ways that could be harmful</li>
                    <li>Parents/guardians should monitor younger teens' activity</li>
                </ul>
            </GuidelineSection>

            <GuidelineSection icon={<Flag className="w-6 h-6" />} title="Reporting Violations" color="purple">
                <p>Help us keep DareUp safe by reporting content that violates these guidelines:</p>
                <ul>
                    <li>Tap the three dots (...) on any video or profile</li>
                    <li>Select "Report" and choose the appropriate reason</li>
                    <li>All reports are anonymous and reviewed by our team</li>
                </ul>
                <p className="mt-4">
                    For urgent safety concerns, contact us directly at{' '}
                    <a href="mailto:abuse@dareup.com" className="text-primary-400 hover:text-primary-300">
                        abuse@dareup.com
                    </a>
                </p>
            </GuidelineSection>

            <section className="mb-10">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-4">Enforcement</h2>
                <div className="text-gray-300 space-y-4">
                    <p>
                        We review reported content and accounts to determine if they violate our guidelines. Actions we
                        may take include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>
                            <strong>Content removal:</strong> Removing videos that violate guidelines
                        </li>
                        <li>
                            <strong>Warnings:</strong> Notifying you of the violation
                        </li>
                        <li>
                            <strong>Feature restrictions:</strong> Limiting your ability to post, comment, or message
                        </li>
                        <li>
                            <strong>Temporary suspension:</strong> Suspending your account for a period of time
                        </li>
                        <li>
                            <strong>Permanent ban:</strong> Removing your account permanently for severe violations
                        </li>
                    </ul>
                    <p>
                        The severity of the action depends on the nature and frequency of violations. We may escalate
                        serious matters to law enforcement.
                    </p>
                </div>
            </section>

            <section className="mb-10">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-4">Appeals</h2>
                <div className="text-gray-300 space-y-4">
                    <p>
                        If you believe your content was removed in error or your account was wrongly restricted, you can
                        appeal the decision:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Check the notification you received for appeal instructions</li>
                        <li>
                            Email{' '}
                            <a href="mailto:appeals@dareup.com" className="text-primary-400 hover:text-primary-300">
                                appeals@dareup.com
                            </a>{' '}
                            with your username and explanation
                        </li>
                        <li>We'll review your appeal and respond within 7 days</li>
                    </ul>
                </div>
            </section>

            <div className="bg-dark-800 rounded-xl p-6 mt-12">
                <h3 className="font-display text-lg font-bold text-white mb-3">Questions?</h3>
                <p className="text-gray-300 mb-4">
                    If you have questions about these guidelines or need to report a concern:
                </p>
                <ul className="text-gray-300 space-y-2">
                    <li>
                        General questions:{' '}
                        <a href="mailto:support@dareup.com" className="text-primary-400 hover:text-primary-300">
                            support@dareup.com
                        </a>
                    </li>
                    <li>
                        Report abuse:{' '}
                        <a href="mailto:abuse@dareup.com" className="text-primary-400 hover:text-primary-300">
                            abuse@dareup.com
                        </a>
                    </li>
                    <li>
                        Copyright issues:{' '}
                        <a href="mailto:dmca@dareup.com" className="text-primary-400 hover:text-primary-300">
                            dmca@dareup.com
                        </a>
                    </li>
                </ul>
            </div>

            <div className="mt-12 pt-8 border-t border-dark-700">
                <p className="text-gray-400 text-sm">
                    These guidelines work alongside our{' '}
                    <a href="/terms" className="text-primary-400 hover:text-primary-300">
                        Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-primary-400 hover:text-primary-300">
                        Privacy Policy
                    </a>
                    . We may update these guidelines as our community grows.
                </p>
            </div>
        </LegalLayout>
    );
}

const colorClasses = {
    green: 'bg-green-500/10 border-green-500/30 text-green-400',
    pink: 'bg-pink-500/10 border-pink-500/30 text-pink-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
};

function GuidelineSection({
    icon,
    title,
    color,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    color: keyof typeof colorClasses;
    children: React.ReactNode;
}) {
    return (
        <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg border ${colorClasses[color]}`}>{icon}</div>
                <h2 className="font-display text-xl sm:text-2xl font-bold text-white">{title}</h2>
            </div>
            <div className="text-gray-300 space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&_li]:text-gray-300">
                {children}
            </div>
        </section>
    );
}
